import { useRef, useState } from "react";
import SignatureCanvas from "react-signature-canvas";
import { Eraser, Upload } from "lucide-react";
import { toast } from "sonner";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { api, fileUrl } from "@/lib/api";

export function SignaturePad({ value, onChange, mode, onModeChange, hasManual }) {
  const padRef = useRef(null);
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);

  const upload = async (blob, name) => {
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", blob, name);
      const { data } = await api.post("/files?kind=signature", fd);
      onChange(data.id);
      onModeChange("digital");
      toast.success("Signature saved");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save signature");
    } finally {
      setBusy(false);
    }
  };

  const saveDrawn = () => {
    if (padRef.current.isEmpty()) return toast.error("Draw a signature first");
    padRef.current.getCanvas().toBlob((b) => upload(b, "signature.png"), "image/png");
  };

  return (
    <div className="panel p-4 space-y-4" data-testid="signature-pad">
      <div className="flex flex-wrap gap-2">
        {[
          ["none", "No signature"],
          ["digital", "Digital"],
          ["manual", "Manual (stored)"],
        ].map(([m, l]) => (
          <button
            key={m}
            type="button"
            data-testid={`signature-mode-${m}`}
            onClick={() => onModeChange(m)}
            disabled={m === "manual" && !hasManual}
            className={`h-8 px-3 text-xs font-semibold border transition-colors disabled:opacity-40 ${mode === m ? "bg-foreground text-background border-foreground" : "border-border hover:border-foreground"}`}
          >
            {l}
          </button>
        ))}
      </div>
      {mode === "manual" && !hasManual && <p className="text-xs text-muted-foreground">Upload a scanned signature in Settings to enable manual signing.</p>}
      {mode === "digital" && (
        <Tabs defaultValue="draw">
          <TabsList className="rounded-none w-full grid grid-cols-2">
            <TabsTrigger value="draw" className="rounded-none" data-testid="signature-tab-draw">Draw</TabsTrigger>
            <TabsTrigger value="upload" className="rounded-none" data-testid="signature-tab-upload">Upload</TabsTrigger>
          </TabsList>
          <TabsContent value="draw" className="space-y-3">
            <div className="border border-border bg-white">
              <SignatureCanvas ref={padRef} penColor="#1a1a1a" canvasProps={{ className: "w-full h-40", "data-testid": "signature-canvas" }} />
            </div>
            <div className="flex gap-2">
              <button type="button" onClick={() => padRef.current.clear()} data-testid="signature-clear" className="h-9 px-3 text-xs font-semibold border border-border hover:border-foreground flex items-center gap-2">
                <Eraser className="w-4 h-4" strokeWidth={1.5} /> Clear
              </button>
              <button type="button" onClick={saveDrawn} disabled={busy} data-testid="signature-save" className="btn-press h-9 px-4 text-xs font-semibold bg-foreground text-background disabled:opacity-50">
                {busy ? "Saving…" : "Use this signature"}
              </button>
            </div>
          </TabsContent>
          <TabsContent value="upload">
            <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp" className="hidden" data-testid="signature-file-input" onChange={(e) => e.target.files[0] && upload(e.target.files[0], e.target.files[0].name)} />
            <button type="button" onClick={() => fileRef.current.click()} disabled={busy} data-testid="signature-upload-button" className="w-full h-24 border border-dashed border-border hover:border-foreground flex flex-col items-center justify-center gap-2 text-xs text-muted-foreground transition-colors">
              <Upload className="w-5 h-5" strokeWidth={1.5} /> {busy ? "Uploading…" : "Choose signature image (PNG/JPG)"}
            </button>
          </TabsContent>
        </Tabs>
      )}
      {mode === "digital" && value && (
        <div className="flex items-center gap-3 text-xs text-muted-foreground">
          <img src={fileUrl(value)} alt="Current signature" className="h-12 bg-white border border-border px-2" crossOrigin="use-credentials" />
          Current digital signature
        </div>
      )}
    </div>
  );
}
